import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Select,
  Box,
} from "@mui/material";
import "../../assets/css/icon.css";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { it } from "date-fns/locale";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import PopupError from "./PopupError";

const datePickerTheme = createTheme({
  palette: {
    primary: {
      main: "#bc2957",
      dark: "#bc2957",
      light: "#bc2957",
    },
  },
});

const ModalReport = ({
  onClose,
  confirm,
  errors = [],
  document,
  setDocument,
  isEdit,
  errorPopup,
  setErrorPopup,
  onDelete,
  onUpdate,
  readers,
}) => {
  const [files, setFiles] = useState([]);
  const [isUploading] = useState(false);
  const [errorFileLarge, setErrorFileLarge] = useState(false);
  const [errorExtensionAccept, setErrorExtensionAccept] = useState(false);

  const [infoModal, setInfoModal] = useState(null);

  const emailSuperReader = process.env.REACT_APP_SUPER_READER_EMAIL;

  useEffect(() => {
    if (document?.original_name) {
      setFiles([{ name: document.original_name, isExisting: true }]);
    } else {
      setFiles(document?.files || []);
    }
  }, [document]);

  useEffect(() => {
    if (document?.reader_id && readers?.length) {
      const readerObj = readers.find((r) => r.id === document.reader_id);

      if (readerObj) {
        setDocument((prev) => ({
          ...prev,
          reader: readerObj,
        }));
      }
    }
  }, [document?.reader_id, readers]);

  const MAX_TOTAL_SIZE = 30 * 1024 * 1024;

  const ACCEPTED_EXT = [
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "docx",
    "msg",
    "eml",
    "txt",
    "odt",
    "dot",
    "xml",
    "pptx",
    "ppt",
    "ppsx",
    "potx",
  ];

  const handleChange = (name, value) => {
    setDocument((prev) => ({ ...prev, [name]: value }));
  };

  const totalSize = (fileList) => fileList.reduce((acc, f) => acc + f.size, 0);

  const validateFiles = (newFiles) => {
    setErrorFileLarge(false);
    setErrorExtensionAccept(false);

    for (const file of newFiles) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ACCEPTED_EXT.includes(ext)) {
        setErrorExtensionAccept(true);
        return false;
      }
    }

    if (totalSize([...files, ...newFiles]) > MAX_TOTAL_SIZE) {
      setErrorFileLarge(true);
      return false;
    }

    return true;
  };

  const addFiles = (newFiles) => {
    const singleFile = [newFiles[0]];
    if (!validateFiles(singleFile)) return;

    setFiles(singleFile);
    handleChange("files", singleFile);
  };

  const handleFileChange = (e) => {
    addFiles(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);

    if (files[index]?.isExisting) {
      handleChange("original_name", null);
    } else {
      handleChange("files", updated);
    }
  };

  return (
    <>
      <Grid
        container
        item
        xs={11}
        md={8}
        sx={{
          borderRadius: "16px",
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#fff",
          width: { xs: "90%", sm: "80%" },
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* HEADER */}
        <Grid
          item
          xs={12}
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #c6c6c6",
            px: "20px",
          }}
        >
          <Grid item xs={1.5} md={0.5} sx={{ ml: 0.5 }}>
            <div className="icon-upload-report-modal" />
          </Grid>

          <Grid item xs={9} md={11}>
            <Typography
              className="color-blue-dark"
              sx={{ fontSize: 18, fontWeight: 600, ml: 1 }}
            >
              {isEdit ? "Modifica report" : " Carica report"}
            </Typography>
          </Grid>

          <Grid
            item
            xs={1.5}
            md={0.5}
            sx={{ display: "flex", justifyContent: "flex-end", mr: 1 }}
          >
            <div onClick={onClose} className="icon-modal-close" />
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* READER */}
            <Grid
              item
              xs={12}
              sm={document?.reader?.email === emailSuperReader ? 4 : 6}
            >
              <Typography
                sx={{
                  fontSize: { xs: "14px !important", md: "16px !important" },
                  mb: { xs: 0.5 },
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                className="color-blue-dark title-input"
              >
                Lettore
                <div
                  className="icon-info-modal-legenda-small"
                  onClick={() => setInfoModal("reader")}
                  style={{ cursor: "pointer", flexShrink: 0 }}
                />
              </Typography>
              <Select
                name="reader"
                value={document?.reader || ""}
                error={errors.includes("reader")}
                onChange={(e) => handleChange("reader", e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">
                  <span style={{ color: "#abb0b6" }}>Seleziona</span>
                </MenuItem>
                {readers
                  ?.filter((r) => r.role === "reader")
                  .map((r) => (
                    <MenuItem key={r.id} value={r}>
                      {r.name}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>

            {/* DATE */}
            <Grid
              item
              xs={12}
              sm={document?.reader?.email === emailSuperReader ? 4 : 6}
            >
              <Typography
                sx={{
                  fontSize: { xs: "14px !important", md: "16px !important" },
                  mb: { xs: 0.5 },
                }}
                className="color-blue-dark title-input"
              >
                Data
              </Typography>
              <ThemeProvider theme={datePickerTheme}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={it}
                >
                  <DatePicker
                    name="datario"
                    value={document?.datario || null}
                    onChange={(newDate) => handleChange("datario", newDate)}
                    format="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={errors.includes("datario")}
                        placeholder="GG/MM/AAAA"
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "GG/MM/AAAA",
                        }}
                        sx={{
                          "& .MuiInputBase-input": { color: "#1b2838" },
                          "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                          "& .MuiIconButton-root:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                    )}
                    PopperProps={{ placement: "bottom-start" }}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>

            {/* SUBJECT READER - visibile solo se reader DEVTEST stg || FD prod è selezionato */}
            {document?.reader?.email === emailSuperReader && (
              <Grid item xs={12} sm={4}>
                <Typography
                  sx={{
                    fontSize: { xs: "14px !important", md: "16px !important" },
                    mb: { xs: 0.5 },
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  className="color-blue-dark title-input"
                >
                  Team
                  <div
                    className="icon-info-modal-legenda-small"
                    onClick={() => setInfoModal("team")}
                    style={{ cursor: "pointer", flexShrink: 0 }}
                  />
                </Typography>
                <Select
                  name="subject_reader_id"
                  value={document?.subject_reader_id || ""}
                  error={errors.includes("subject_reader_id")}
                  onChange={(e) =>
                    handleChange("subject_reader_id", e.target.value)
                  }
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="">
                    <span style={{ color: "#abb0b6" }}>Seleziona</span>
                  </MenuItem>
                  {readers.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            )}

            {/* UPLOAD */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Grid
                sx={{
                  backgroundColor: "#d3d3d382",
                  height: 100,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: "10px",
                  border:
                    errorFileLarge ||
                    errorExtensionAccept ||
                    errors.includes("files")
                      ? "1px solid #bc2957"
                      : "none",
                }}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() =>
                  window.document.getElementById("file-input")?.click()
                }
              >
                <div className="icon-upload-document" />

                <input
                  id="file-input"
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />

                {isUploading && <CircularProgress sx={{ mt: 2 }} />}

                {files.length === 0 && (
                  <Typography
                    sx={{ fontSize: { xs: "12px", md: "14px" } }}
                    className="color-blue-dark"
                  >
                    Seleziona o trascina qui il file da caricare
                  </Typography>
                )}

                {files.length > 0 && (
                  <Grid sx={{ mt: 1, width: "100%" }}>
                    {files.map((f, index) => (
                      <Grid
                        key={index}
                        container
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Typography className="color-blue-dark" sx={{ mr: 1 }}>
                          {f.name}{" "}
                        </Typography>
                        <div
                          className="icon-remove-file"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}

                {errorFileLarge && (
                  <Typography className="color-purple" sx={{ mt: 1 }}>
                    Dimensione totale file superiore a 30MB
                  </Typography>
                )}

                {errorExtensionAccept && (
                  <Typography className="color-purple" sx={{ mt: 1 }}>
                    Estensione file non consentita
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* FOOTER */}
          <Grid
            container
            sx={{
              mt: 4,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* ELIMINA */}
            {isEdit ? (
              <Button
                className="button-bg-empty"
                variant="outlined"
                sx={{
                  width: { xs: "120px !important", sm: "160px !important" },
                  height: "40px",
                  borderRadius: "10px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                }}
                onClick={() => onDelete?.(document)}
              >
                <Box
                  className="icon-delete-report-button"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: "-2px",
                  }}
                />
                <Typography
                  className="color-blue-dark"
                  sx={{
                    textTransform: "initial",
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: 500,
                  }}
                >
                  Elimina
                </Typography>
              </Button>
            ) : (
              <Box />
            )}

            {/* CARICA / MODIFICA */}
            <Button
              className="button-bg-purple"
              sx={{
                width: { xs: "120px !important", sm: "160px !important" },
                height: "40px",
                border: "none !important",
                borderRadius: "10px !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
              }}
              onClick={
                isEdit ? () => onUpdate?.(document) : () => confirm?.(document)
              }
            >
              <Box
                className={
                  isEdit
                    ? "icon-save-report-button"
                    : "icon-upload-report-button"
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mt: "-2px",
                }}
              />
              <Typography
                className="color-light"
                sx={{
                  textTransform: "initial",
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 500,
                }}
              >
                {isEdit ? "Salva" : "Carica"}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {errorPopup && (
        <PopupError
          copyError="Uno o più campi obbligatori sono mancanti"
          closePopupError={() => setErrorPopup(false)}
        />
      )}

      {infoModal && (
        <>
          <div
            onClick={() => setInfoModal(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />

          <Grid
            container
            item
            xs={11}
            md={5}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#fff",
              maxHeight: "100%",
              width: { xs: "90%", sm: "80%" },
              borderRadius: "16px",
              zIndex: 1300,
            }}
          >
            {/* Header */}
            <Grid
              item
              xs={12}
              style={{
                height: "60px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #c6c6c6",
              }}
            >
              <Grid item xs={0.5} style={{ marginLeft: "15px" }}>
                <div className="icon-info-modal-legenda" />
              </Grid>
              <Grid item xs={11} sx={{ ml: 1 }}>
                <Typography
                  className="color-blue-dark"
                  style={{
                    fontSize: "18px",
                    marginTop: "3px",
                    fontWeight: 600,
                  }}
                >
                  {infoModal === "reader" ? "Lettore" : "Team"}
                </Typography>
              </Grid>
              <Grid
                item
                xs={0.5}
                style={{
                  marginRight: "15px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <div
                  onClick={() => setInfoModal(null)}
                  className="icon-modal-close"
                />
              </Grid>
            </Grid>

            {/* Body */}
            <Grid
              item
              xs={12}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Typography
                style={{ padding: "10px 15px 0px 15px", marginTop: "20px" }}
                className="color-blue-dark"
              >
                {/* <strong>{infoModal === "reader" ? "Lettore" : "Team"}</strong>:{" "} */}
                {infoModal === "reader"
                  ? "Il lettore è la persona che avrà accesso al report."
                  : "Il team indica la persona la cui area di competenza è oggetto del report."}
              </Typography>
            </Grid>

            {/* Footer */}
            <Grid container item xs={12} style={{ marginTop: "20px" }}>
              <Grid
                item
                xs={12}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "20px",
                }}
              >
                <Button
                  className="button-bg-purple"
                  onClick={() => setInfoModal(null)}
                >
                  <Typography
                    className="color-light"
                    sx={{
                      textTransform: "initial",
                      fontSize: { xs: "16px", sm: "18px" },
                      fontWeight: 500,
                    }}
                  >
                    Chiudi
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default ModalReport;
